export const OTP_URL_AUTH_FLAG_KEY = 'ac_otp_url_auth';

let otpCredentialAuthInFlight: Promise<void> | undefined;

export const PORTAL_BOOT_TIMEOUT_MILLISECONDS = 4_000;
export const LOGIN_REQUEST_TIMEOUT_MILLISECONDS = 10_000;

export const EOtpAuthReasonCode = {
  LOGIN_REQUEST_FAILED: 'login-request-failed',
  LOGIN_TIMEOUT: 'login-timeout',
  LOGIN_NETWORK: 'login-network',
  CREDENTIAL_EXPIRED: 'credential-expired',
  CREDENTIAL_INVALID: 'credential-invalid',
  BOOT_FAILED: 'boot-failed',
  USER_CANCELLED: 'user-cancelled',
} as const;

export type OtpAuthReasonCode = (typeof EOtpAuthReasonCode)[keyof typeof EOtpAuthReasonCode];

export const stripCredentialQueryParams = (href: string): string => {
  const url = new URL(href);
  url.searchParams.delete('proofKey');
  url.searchParams.delete('token');
  return `${url.pathname}${url.search}${url.hash}`;
};

export const shareOtpCredentialAuth = (run: () => Promise<void>): Promise<void> => {
  if (!otpCredentialAuthInFlight) {
    otpCredentialAuthInFlight = run().finally(() => {
      otpCredentialAuthInFlight = undefined;
    });
  }

  return otpCredentialAuthInFlight;
};

export const joinOtpCredentialAuth = (): Promise<void> | undefined => otpCredentialAuthInFlight;

export const markOtpUrlAuthActive = (): void => {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  try {
    sessionStorage.setItem(OTP_URL_AUTH_FLAG_KEY, '1');
  } catch {
    return;
  }
};

export const isOtpUrlAuthActive = (): boolean => {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }

  try {
    return sessionStorage.getItem(OTP_URL_AUTH_FLAG_KEY) === '1';
  } catch {
    return false;
  }
};

export const clearOtpUrlAuth = (): void => {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  try {
    sessionStorage.removeItem(OTP_URL_AUTH_FLAG_KEY);
  } catch {
    return;
  }
};

export const shouldSkipStaleOtpRoundTripReconcile = (
  isOtpCredentialLanding: boolean = false
): boolean => {
  if (isOtpCredentialLanding) {
    return true;
  }

  if (isOtpUrlAuthActive()) {
    return true;
  }

  if (joinOtpCredentialAuth()) {
    return true;
  }

  return false;
};

export const readOtpCredentialsFromLocation = (
  href: string
): { proofKey: string; token: string } | undefined => {
  const url = new URL(href);
  const proofKey = url.searchParams.get('proofKey');
  const token = url.searchParams.get('token');
  if (!proofKey || !token) {
    return undefined;
  }

  return { proofKey, token };
};

export const classifyOtpBusinessFailureReason = (message: string = ''): OtpAuthReasonCode =>
  /\bexpir(?:ed|y|ation)\b/i.test(message)
    ? EOtpAuthReasonCode.CREDENTIAL_EXPIRED
    : EOtpAuthReasonCode.CREDENTIAL_INVALID;

export const classifyLoginFailureReason = (error: unknown): OtpAuthReasonCode => {
  const axiosError = error as {
    code?: string;
    message?: string;
    response?: { status?: number; data?: { message?: string } };
  };

  if (
    axiosError?.code === 'ECONNABORTED' ||
    axiosError?.message === 'timeout' ||
    /timeout/i.test(axiosError?.message || '')
  ) {
    return EOtpAuthReasonCode.LOGIN_TIMEOUT;
  }

  if (!axiosError?.response) {
    return EOtpAuthReasonCode.LOGIN_NETWORK;
  }

  const status = axiosError.response.status;
  const message = axiosError.response.data?.message || axiosError.message || '';

  if (status !== undefined && status >= 400 && status < 500) {
    return classifyOtpBusinessFailureReason(message);
  }

  return EOtpAuthReasonCode.LOGIN_REQUEST_FAILED;
};

export const isTransportLoginFailure = (reasonCode: OtpAuthReasonCode): boolean =>
  reasonCode === EOtpAuthReasonCode.LOGIN_TIMEOUT ||
  reasonCode === EOtpAuthReasonCode.LOGIN_NETWORK;
